import { Trans, useTranslation } from "react-i18next";
import { motion } from "motion/react";

import { ScrollArea } from "@/components/ui/scroll-area";
import LanguageSwitcher from "@/layouts/AuthLayout/components/LanguageSwitcher/LanguageSwitcher.tsx";
import AppLogo from "@/components/Logo.tsx";

const TermsOfService = () => {
  useTranslation();

  return (
    <div className="bg-background min-h-dvh">
      <div className="absolute top-2 right-2 z-50 md:top-10 md:right-10">
        <LanguageSwitcher />
      </div>
      <ScrollArea className="h-dvh">
        <div className="mx-auto max-w-4xl p-6">
          <motion.div
            animate={{ opacity: 0.05 }}
            className="absolute -top-20 -left-20 h-56 w-56 rounded-full bg-gradient-to-br from-teal-400 to-purple-500"
            initial={{ opacity: 0.05 }}
            layoutId={"blue"}
            transition={{ layout: { duration: 0.75 } }}
          />
          <motion.div
            animate={{ opacity: 0.05 }}
            className="absolute -right-16 -bottom-16 h-40 w-40 rounded-full bg-gradient-to-tr from-red-400 to-pink-600"
            initial={{ opacity: 0.05 }}
            layoutId={"purple"}
            transition={{ layout: { duration: 0.75 } }}
          />
          <div className="relative">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header with Logo */}
              <div className="py-8 text-center">
                <AppLogo className="mx-auto mb-6 h-12" />
                <h1 className="text-accent-foreground mb-4 text-3xl font-bold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="title"
                    ns="terms_of_service"
                  />
                </h1>
                <p className="text-muted-foreground text-sm">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="legalNote"
                    ns="terms_of_service"
                  />
                </p>
              </div>

              {/* Chapter 1 */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="chapter1.title"
                    ns="terms_of_service"
                  />
                </h2>

                {/* Section 1 - Definitions */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter1.section1.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 22 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: pointNumber * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                            link1: (
                              <a
                                key="1"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:support@ximly.app"
                              />
                            ),
                            link2: (
                              <a
                                key="2"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:legal@ximly.app"
                              />
                            ),
                          }}
                          //@ts-ignore
                          i18nKey={`chapter1.section1.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Section 2 - General Provisions */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter1.section2.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 22) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                            link1: (
                              <a
                                key="1"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="https://www.ximly.app"
                                rel="noopener noreferrer"
                                target="_blank"
                              />
                            ),
                            link2: (
                              <a
                                key="2"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:support@ximly.app"
                              />
                            ),
                            link3: (
                              <a
                                key="2"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:legal@ximly.app"
                              />
                            ),
                          }}
                          //@ts-ignore
                          i18nKey={`chapter1.section2.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                      </motion.li>
                    ))}
                  </ol>
                </div>
              </div>

              {/* Chapter 2 */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="chapter2.title"
                    ns="terms_of_service"
                  />
                </h2>

                {/* Section 3 - Technical Requirements */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter2.section3.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 3 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 34) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                            link1: (
                              <a
                                key="1"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:support@ximly.app"
                              />
                            ),
                          }}
                          //@ts-ignore
                          i18nKey={`chapter2.section3.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                        {pointNumber === 3 && (
                          <ul className="mt-2 ml-6 space-y-2">
                            {["a", "b", "c", "d", "e", "f"].map((subpoint) => (
                              <li key={subpoint} className="text-sm">
                                <span className="text-accent-foreground font-semibold">
                                  {subpoint})
                                </span>{" "}
                                <Trans
                                  components={{
                                    b: <span key={"3"} className={"font-bold"} />,
                                  }}
                                  //@ts-ignore
                                  i18nKey={`chapter2.section3.subpoint_${subpoint}`}
                                  ns="terms_of_service"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Section 4 - Using the Platform */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter2.section4.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 16 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 37) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                            link1: (
                              <a
                                key="1"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:support@ximly.app"
                              />
                            ),
                          }}
                          //@ts-ignore
                          i18nKey={`chapter2.section4.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                        {pointNumber === 5 && (
                          <ul className="mt-2 ml-6 space-y-2">
                            {["a", "b", "c", "d"].map((subpoint) => (
                              <li key={subpoint} className="text-sm">
                                <span className="text-accent-foreground font-semibold">
                                  {subpoint})
                                </span>{" "}
                                <Trans
                                  components={{
                                    b: <span key={"3"} className={"font-bold"} />,
                                  }}
                                  //@ts-ignore
                                  i18nKey={`chapter2.section4.subpoint_${subpoint}`}
                                  ns="terms_of_service"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                        {pointNumber === 6 && (
                          <ul className="mt-2 ml-6 space-y-2">
                            {["6a", "6b", "6c"].map((subpoint) => (
                              <li key={subpoint} className="text-sm">
                                <span className="text-accent-foreground font-semibold">
                                  {subpoint.replace("6", "")})
                                </span>{" "}
                                <Trans
                                  components={{
                                    b: <span key={"3"} className={"font-bold"} />,
                                  }}
                                  //@ts-ignore
                                  i18nKey={`chapter2.section4.subpoint_${subpoint}`}
                                  ns="terms_of_service"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Section 5 - User Account */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter2.section5.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 21 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 53) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                            link1: (
                              //@ts-ignore
                              <a
                                key="1"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:support@ximly.app"
                              />
                            ),
                          }}
                          //@ts-ignore
                          i18nKey={`chapter2.section5.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                      </motion.li>
                    ))}
                  </ol>
                </div>
              </div>

              {/* Chapter 3 */}
              <div className="space-y-8">
                <h2 className="text-accent-foreground text-center text-xl font-semibold">
                  <Trans
                    components={{
                      b: <span key={"3"} className={"font-bold"} />,
                    }}
                    i18nKey="chapter3.title"
                    ns="terms_of_service"
                  />
                </h2>

                {/* Section 6 - Using Services */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter3.section6.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 74) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                          }}
                          //@ts-ignore
                          i18nKey={`chapter3.section6.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Section 7 - Test Period */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter3.section7.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 7 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 84) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                          }}
                          //@ts-ignore
                          i18nKey={`chapter3.section7.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                        {pointNumber === 5 && (
                          <ul className="mt-2 ml-6 space-y-2">
                            {["a", "b", "c", "d", "e"].map((subpoint) => (
                              <li key={subpoint} className="text-sm">
                                <span className="text-accent-foreground font-semibold">
                                  {subpoint})
                                </span>{" "}
                                <Trans
                                  components={{
                                    b: <span key={"3"} className={"font-bold"} />,
                                  }}
                                  //@ts-ignore
                                  i18nKey={`chapter3.section7.subpoint_${subpoint}`}
                                  ns="terms_of_service"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Section 8 - Payments */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter3.section8.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 21 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 91) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                          }}
                          //@ts-ignore
                          i18nKey={`chapter3.section8.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Section 9 - Complaints */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter3.section9.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 7 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 112) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                            link1: (
                              //@ts-ignore
                              <a
                                key="1"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:support@ximly.app"
                              />
                            ),
                            link2: (
                              //@ts-ignore
                              <a
                                key="1"
                                className="text-accent-foreground hover:text-accent-foreground/80 underline"
                                href="mailto:legal@ximly.app"
                              />
                            ),
                          }}
                          //@ts-ignore
                          i18nKey={`chapter3.section9.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                        {pointNumber === 4 && (
                          <ul className="mt-2 ml-6 space-y-2">
                            {["a", "b", "c", "d"].map((subpoint) => (
                              <li key={subpoint} className="text-sm">
                                <span className="text-accent-foreground font-semibold">
                                  {subpoint})
                                </span>{" "}
                                <Trans
                                  components={{
                                    b: <span key={"3"} className={"font-bold"} />,
                                  }}
                                  //@ts-ignore
                                  i18nKey={`chapter3.section9.subpoint_${subpoint}`}
                                  ns="terms_of_service"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Section 10 - Lesson Recording */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter3.section10.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 5 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 119) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                          }}
                          //@ts-ignore
                          i18nKey={`chapter3.section10.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                        {pointNumber === 2 && (
                          <ul className="mt-2 ml-6 space-y-2">
                            {["a", "b", "c"].map((subpoint) => (
                              <li key={subpoint} className="text-sm">
                                <span className="text-accent-foreground font-semibold">
                                  {subpoint})
                                </span>{" "}
                                <Trans
                                  components={{
                                    b: <span key={"3"} className={"font-bold"} />,
                                  }}
                                  //@ts-ignore
                                  i18nKey={`chapter3.section10.subpoint_${subpoint}`}
                                  ns="terms_of_service"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Section 11 - Agreement Conclusion */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter3.section11.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 3 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 124) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                          }}
                          //@ts-ignore
                          i18nKey={`chapter3.section11.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                      </motion.li>
                    ))}
                  </ol>
                </div>

                {/* Final Provisions */}
                <div className="space-y-6">
                  <h3 className="text-accent-foreground text-center text-lg font-semibold">
                    <Trans
                      components={{
                        b: <span key={"3"} className={"font-bold"} />,
                      }}
                      i18nKey="chapter3.finalProvisions.title"
                      ns="terms_of_service"
                    />
                  </h3>
                  <ol className="space-y-4">
                    {Array.from({ length: 6 }, (_, i) => i + 1).map((pointNumber) => (
                      <motion.li
                        key={pointNumber}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm leading-relaxed"
                        initial={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3, delay: (pointNumber + 127) * 0.05 }}
                      >
                        <span className="text-accent-foreground font-semibold">{pointNumber}.</span>{" "}
                        <Trans
                          components={{
                            b: <span key={"3"} className={"font-bold"} />,
                          }}
                          //@ts-ignore
                          i18nKey={`chapter3.finalProvisions.point${pointNumber}`}
                          ns="terms_of_service"
                        />
                      </motion.li>
                    ))}
                  </ol>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default TermsOfService;
